import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { feedbackApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { MessageSquarePlus, Send, X } from 'lucide-react';

const MIN_LENGTH = 5;
const MAX_LENGTH = 1000;

const FeedbackWidget = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!expanded) return;

    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [expanded]);

  const handleToggle = () => {
    if (!isAuthenticated) {
      navigate('/login', {
        state: { from: location.pathname, message: 'Login for the feedback' }
      });
      return;
    }
    setExpanded((prev) => !prev);
  };

  const handleCancel = () => {
    setExpanded(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();

    if (trimmed.length < MIN_LENGTH || trimmed.length > MAX_LENGTH) {
      toast.error(`Feedback message must be between ${MIN_LENGTH} and ${MAX_LENGTH} characters.`);
      return;
    }

    setSubmitting(true);
    try {
      await feedbackApi.create({
        message: trimmed,
        page_url: location.pathname
      });
      toast.success('Thanks for your feedback!');
      setMessage('');
      setExpanded(false);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={containerRef}
      className="bg-aluminum border-t border-chassis/10"
      data-testid="feedback-widget"
      aria-label="Site feedback"
    >
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-4">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-controls="feedback-widget-panel"
          className="w-full flex items-center justify-between gap-3 text-left font-chivo text-sm font-bold uppercase tracking-wider text-chassis hover:text-autoxium-blue transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-autoxium-blue rounded-sm py-1"
          data-testid="feedback-widget-toggle"
        >
          <span className="flex items-center gap-2">
            <MessageSquarePlus className="h-4 w-4 shrink-0" />
            Help us to improve your experience — Share your feedback
          </span>
          {expanded && <X className="h-4 w-4" aria-hidden="true" />}
        </button>

        {expanded && (
          <div id="feedback-widget-panel" className="mt-4">
            <form
              onSubmit={handleSubmit}
              className="grid gap-4 max-w-2xl"
              data-testid="feedback-widget-form"
            >
              <div className="space-y-1.5">
                <Label htmlFor="feedback-message">
                  Feedback <span aria-hidden="true">*</span>
                </Label>
                <Textarea
                  id="feedback-message"
                  rows={4}
                  required
                  aria-required="true"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What's on your mind? Bug reports, suggestions, anything helps."
                  data-testid="feedback-widget-message-input"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {message.trim().length}/{MAX_LENGTH}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                  data-testid="feedback-widget-submit-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {submitting ? 'Sending...' : 'Submit'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={submitting}
                  data-testid="feedback-widget-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeedbackWidget;
